import { apiSlice } from './apiSlice';
import { authApi } from './authApi';
import type { User, Role, RolesAccessPayload } from '../../types';
import { updateUser } from '../slices/authSlice';

export const usersApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => ({ url: '/users' }),
      providesTags: ['User'],
      transformResponse: (response: any) => response?.data || response || []
    }),
    getRolesList: builder.query<Role[], void>({
      query: () => ({ url: '/users/roles/list' }),
      providesTags: ['Auth'],
      transformResponse: (response: any) => response?.data || response || []
    }),
    createUser: builder.mutation<User, Partial<User & { password: string }>>({
      query: (body) => ({ url: '/users', method: 'POST', body }),
      invalidatesTags: ['User']
    }),
    updateUser: builder.mutation<User, { id: number; body: Partial<User> }>({
      query: ({ id, body }) => ({ url: `/users/${id}`, method: 'PUT', body }),
      invalidatesTags: ['User']
    }),
    activateUser: builder.mutation<any, number>({
      query: (id) => ({ url: `/users/${id}/activate`, method: 'PATCH' }),
      invalidatesTags: ['User']
    }),
    deactivateUser: builder.mutation<any, number>({
      query: (id) => ({ url: `/users/${id}/deactivate`, method: 'PATCH' }),
      invalidatesTags: ['User']
    }),
    resetUserPassword: builder.mutation<any, { id: number; newPassword?: string }>({
      query: ({ id, newPassword }) => ({ url: `/users/${id}/reset-password`, method: 'POST', body: { newPassword } }),
      invalidatesTags: ['User']
    }),
    // Roles management
    createRole: builder.mutation<Role, { name: string; description?: string; permissions?: any }>({
      query: (body) => ({ url: '/users/roles', method: 'POST', body }),
      invalidatesTags: ['Auth', 'RoleCatalog']
    }),
    updateRole: builder.mutation<Role, { id: number; body: { name?: string; description?: string; permissions?: any } }>({
      query: ({ id, body }) => ({ url: `/users/roles/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Auth', 'RoleCatalog'],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (arg.body?.permissions !== undefined) {
            try {
              const u = await dispatch(authApi.endpoints.getProfile.initiate(undefined, { forceRefetch: true })).unwrap();
              dispatch(updateUser(u));
            } catch {
              /* ignore */
            }
          }
        } catch {
          /* mutation failed */
        }
      }
    }),
    deleteRole: builder.mutation<any, number>({
      query: (id) => ({ url: `/users/roles/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Auth', 'RoleCatalog']
    }),
    /** GET /roles — каталог прав и роли (только admin) */
    getRolesAccess: builder.query<RolesAccessPayload, void>({
      query: () => ({ url: '/roles' }),
      providesTags: ['RoleCatalog'],
      transformResponse: (response: any): RolesAccessPayload => response?.data ?? response
    }),
    /** PUT /roles/:id/permissions — только whitelist-ключи */
    updateRolePermissions: builder.mutation<Role, { id: number; permissions: Record<string, boolean> }>({
      query: ({ id, permissions }) => ({
        url: `/roles/${id}/permissions`,
        method: 'PUT',
        body: { permissions }
      }),
      invalidatesTags: ['RoleCatalog', 'Auth'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          try {
            const u = await dispatch(authApi.endpoints.getProfile.initiate(undefined, { forceRefetch: true })).unwrap();
            dispatch(updateUser(u));
          } catch {
            /* ignore */
          }
        } catch {
          /* mutation failed */
        }
      }
    })
  })
});

export const {
  useGetUsersQuery,
  useGetRolesListQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useActivateUserMutation,
  useDeactivateUserMutation,
  useResetUserPasswordMutation,
  // roles hooks
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetRolesAccessQuery,
  useUpdateRolePermissionsMutation
} = usersApi;
